"use client"

import { useMemo, useState } from "react"
import { Check, X, ChevronDown, ChevronRight, GitCompare } from "lucide-react"
import { computeHunks, applyHunks, summarizeHunks, type Hunk } from "../lib/hunk-diff"

/** One pending file change awaiting per-hunk review (mirrors the IDE's PendingDiff shape). */
export interface PendingDiff {
  id: string
  path: string
  oldContent: string
  newContent: string
  description?: string
}

interface DiffReviewModalProps {
  diffs: PendingDiff[]
  /** Accept the per-hunk selection for a file → write the resolved content. */
  onApply: (id: string, resolvedContent: string) => void
  /** Discard the whole file change (revert to old content). */
  onReject: (id: string) => void
  /** Accept every selected hunk across all files. */
  onApplyAll: (resolved: Array<{ id: string; content: string }>) => void
  onRejectAll: () => void
}

function Line({ kind, text }: { kind: "context" | "remove" | "add"; text: string }) {
  return (
    <div
      className={`px-3 py-px whitespace-pre-wrap break-words ${
        kind === "add"
          ? "bg-green-500/10 text-green-400"
          : kind === "remove"
            ? "bg-red-500/10 text-red-400"
            : "text-muted-foreground"
      }`}
    >
      <span className="mr-2 select-none opacity-50">{kind === "add" ? "+" : kind === "remove" ? "-" : " "}</span>
      {text}
    </div>
  )
}

/** A single hunk with its accept/reject toggle (the per-hunk staging control). */
function HunkBlock({ hunk, accepted, onToggle }: { hunk: Hunk; accepted: boolean; onToggle: () => void }) {
  return (
    <div className={`border-t border-border ${accepted ? "" : "opacity-50"}`}>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-3 py-1 bg-muted/40 text-left hover:bg-muted transition-colors"
        title={accepted ? "Hunk staged — click to skip" : "Hunk skipped — click to stage"}
      >
        <span
          className={`flex h-3.5 w-3.5 items-center justify-center rounded-sm border text-[9px] ${
            accepted ? "bg-green-500/70 border-green-500 text-white" : "border-border text-transparent"
          }`}
        >
          <Check className="h-2.5 w-2.5" />
        </span>
        <span className="text-[10px] text-muted-foreground">
          @@ hunk {hunk.id} — +{hunk.newLines.length} / -{hunk.oldLines.length}
        </span>
      </button>
      <div className="max-h-48 overflow-y-auto">
        {hunk.contextBefore.map((c, i) => <Line key={`cb${i}`} kind="context" text={c} />)}
        {hunk.oldLines.map((c, i) => <Line key={`r${i}`} kind="remove" text={c} />)}
        {hunk.newLines.map((c, i) => <Line key={`a${i}`} kind="add" text={c} />)}
        {hunk.contextAfter.map((c, i) => <Line key={`ca${i}`} kind="context" text={c} />)}
      </div>
    </div>
  )
}

function FileDiff({
  diff,
  accepted,
  setAccepted,
}: {
  diff: PendingDiff
  accepted: Set<string>
  setAccepted: (ids: Set<string>) => void
}) {
  const [collapsed, setCollapsed] = useState(false)
  const hunks = useMemo(() => computeHunks(diff.oldContent, diff.newContent), [diff.oldContent, diff.newContent])
  const summary = useMemo(() => summarizeHunks(hunks), [hunks])

  const toggle = (id: string) => {
    const next = new Set(accepted)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setAccepted(next)
  }

  return (
    <div className="rounded border border-border overflow-hidden text-[11px] font-mono">
      <button
        className="flex items-center gap-2 w-full px-3 py-1.5 bg-muted/40 border-b border-border text-left hover:bg-muted transition-colors"
        onClick={() => setCollapsed((v) => !v)}
      >
        {collapsed ? <ChevronRight className="h-3 w-3 shrink-0" /> : <ChevronDown className="h-3 w-3 shrink-0" />}
        <span className="flex-1 truncate text-foreground font-semibold">{diff.path}</span>
        <span className="text-[10px] text-muted-foreground mr-1">{accepted.size}/{summary.hunks} hunks</span>
        <span className="text-green-400 text-[10px]">+{summary.added}</span>
        <span className="text-muted-foreground text-[10px] mx-1">/</span>
        <span className="text-red-400 text-[10px]">-{summary.removed}</span>
      </button>
      {!collapsed &&
        hunks.map((h) => (
          <HunkBlock key={h.id} hunk={h} accepted={accepted.has(h.id)} onToggle={() => toggle(h.id)} />
        ))}
    </div>
  )
}

export function DiffReviewModal({ diffs, onApply, onReject, onApplyAll, onRejectAll }: DiffReviewModalProps) {
  const [selection, setSelection] = useState<Record<string, Set<string>>>(() => {
    const init: Record<string, Set<string>> = {}
    for (const d of diffs) init[d.id] = new Set(computeHunks(d.oldContent, d.newContent).map((h) => h.id))
    return init
  })

  if (diffs.length === 0) return null

  const setFileSelection = (id: string, ids: Set<string>) => setSelection((prev) => ({ ...prev, [id]: ids }))

  const resolveFile = (d: PendingDiff): string => {
    const hunks = computeHunks(d.oldContent, d.newContent)
    return applyHunks(d.oldContent, hunks, selection[d.id] ?? new Set<string>())
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-popover border border-border rounded-lg shadow-2xl w-[760px] max-w-[96vw] max-h-[82vh] flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <GitCompare className="h-4 w-4 text-primary shrink-0" />
          <span className="font-semibold text-foreground">Diff Review</span>
          <span className="ml-1 text-[11px] text-muted-foreground">
            {diffs.length} file{diffs.length !== 1 ? "s" : ""} · per-hunk
          </span>
          <div className="flex-1" />
          <button
            onClick={() => onApplyAll(diffs.map((d) => ({ id: d.id, content: resolveFile(d) })))}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors border border-green-500/30"
          >
            <Check className="h-3 w-3" /> Keep selected (all)
          </button>
          <button
            onClick={onRejectAll}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors border border-red-500/30 ml-1"
          >
            <X className="h-3 w-3" /> Discard all
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {diffs.map((diff) => (
            <div key={diff.id}>
              {diff.description && <p className="text-[11px] text-muted-foreground mb-1">{diff.description}</p>}
              <FileDiff
                diff={diff}
                accepted={selection[diff.id] ?? new Set<string>()}
                setAccepted={(ids) => setFileSelection(diff.id, ids)}
              />
              <div className="flex gap-2 mt-1.5">
                <button
                  onClick={() => onApply(diff.id, resolveFile(diff))}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1 text-xs bg-green-500/15 text-green-400 rounded hover:bg-green-500/25 transition-colors border border-green-500/20"
                >
                  <Check className="h-3 w-3" /> Keep selected hunks
                </button>
                <button
                  onClick={() => onReject(diff.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1 text-xs bg-red-500/15 text-red-400 rounded hover:bg-red-500/25 transition-colors border border-red-500/20"
                >
                  <X className="h-3 w-3" /> Discard file
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-4 py-2 border-t border-border text-[10px] text-muted-foreground">
          Per-hunk review — untick a hunk to skip it. &ldquo;Keep selected hunks&rdquo; writes only the staged
          hunks; &ldquo;Discard file&rdquo; reverts the agent&rsquo;s change.
        </div>
      </div>
    </div>
  )
}
